import { createContext, useContext, useState, useRef, useCallback, ReactNode } from "react";
import { X, ShoppingBag, ArrowRight } from "lucide-react";
import { NavLink } from "react-router-dom";
import type { Product } from "@/data/products";
import { useCart } from "@/lib/cartContext";
import { cn } from "@/lib/utils";

interface ToastContextValue {
  showToast: (product: Product) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

function ToastUI({ product, onClose }: { product: Product; onClose: () => void }) {
  const { openCart } = useCart();
  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : null;

  return (
    <div className="fixed top-20 right-4 z-[60] w-80 bg-white border border-stone-100 shadow-xl animate-in slide-in-from-right-4 duration-300">
      <div className="flex items-start gap-3 p-4">
        {/* Image */}
        <div className="w-14 h-18 flex-shrink-0 overflow-hidden bg-stone-100" style={{ height: "4.5rem" }}>
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-[9px] tracking-widest uppercase text-stone-400 mb-0.5">
            Aggiunto al carrello
          </p>
          <p className="text-[10px] tracking-widest uppercase text-stone-400 mb-0.5">
            {product.brand || product.category}
          </p>
          <p className="text-sm font-medium text-stone-900 leading-snug truncate">{product.name}</p>
          <div className="flex items-center gap-1.5 mt-1">
            <span className="text-sm font-semibold text-stone-900">€{product.price.toLocaleString("it-IT")}</span>
            {discount && <span className="text-[10px] text-red-500 font-medium">–{discount}%</span>}
          </div>
        </div>

        {/* Close */}
        <button onClick={onClose} className="flex-shrink-0 text-stone-300 hover:text-stone-900 transition-colors p-0.5">
          <X className="size-3.5" />
        </button>
      </div>

      {/* Actions */}
      <div className="border-t border-stone-100 grid grid-cols-2">
        <NavLink
          to="/cart"
          onClick={onClose}
          className="flex items-center justify-center gap-1.5 py-3 text-[10px] tracking-widest uppercase text-stone-600 hover:text-stone-900 transition-colors border-r border-stone-100"
        >
          <ShoppingBag className="size-3" />
          Carrello
        </NavLink>
        <button
          onClick={() => { onClose(); openCart(); }}
          className="group flex items-center justify-center gap-1.5 py-3 text-[10px] tracking-widest uppercase bg-stone-900 text-white hover:bg-stone-700 transition-colors"
        >
          Checkout
          <ArrowRight className="size-3 group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<Product | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((product: Product) => {
    if (timer.current) clearTimeout(timer.current);
    setCurrent(product);
    timer.current = setTimeout(() => setCurrent(null), 3000);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {current && (
        <ToastUI
          product={current}
          onClose={() => {
            if (timer.current) clearTimeout(timer.current);
            setCurrent(null);
          }}
        />
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}
